import axios, { CancelToken } from 'axios';

import { DVF_PATH, DVF_TRANSACTION_FEATURE_ID, TRANSIT_STATION_FEATURE_ID } from '../constants.es6';
import { isMobile } from '../device';
import MAEvent from '../ma-event';
import HTTPService from '../services/HTTPService.es6';
import spinnerTemplate from '../templates/spinner-dots.html';

import * as MapHelpers from './helpers.es6';
import MapboxGL from './mapbox-gl-loader.es6';

/**
 * Build URL to get the InfoWindow content
 * Calculate bounds around the feature to grab other features at same coordinates
 * @param {String} feature GeoJson feature
 * @param {Object} params URL params. Bounds will be injected automatically
 * @return {{url: String, params: Object}} Url with query params for fetching feature popup content
 */
function getRequestForFeature(feature, params) {
    const {
        properties: { url, type, coordinates },
    } = feature;

    const request = { url, params };
    const { lng, lat } = typeof coordinates === 'string' ? JSON.parse(coordinates) : coordinates;

    if (![TRANSIT_STATION_FEATURE_ID, DVF_TRANSACTION_FEATURE_ID].includes(type)) {
        const latOffset = 0.00003;
        const lngOffset = 0.00004;
        request.url = '/carte/infowindow';
        request.params.bounds = MapHelpers.getBoundsString(
            new MapboxGL.LngLatBounds(
                new MapboxGL.LngLat(lng - lngOffset, lat + latOffset),
                new MapboxGL.LngLat(lng + lngOffset, lat - latOffset),
            ),
        );
    } else if (type === DVF_TRANSACTION_FEATURE_ID) {
        request.url = `${DVF_PATH}/infowindow`;
        request.params.bounds = MapHelpers.getBoundsString(
            new MapboxGL.LngLatBounds(new MapboxGL.LngLat(lng, lat), new MapboxGL.LngLat(lng, lat)),
        );
    }

    return request;
}

/**
 * Handler to cancel a infowindow request if one is executing
 */
let cancelRequest;

/**
 * Get content to fill popup on over
 * @param {String} feature GeoJson feature
 * @param {Object} params URL params
 * @return {Promise}
 */
function fetchPopupContent(feature, params) {
    const request = getRequestForFeature(feature, params);
    return HTTPService.get(request.url, request.params, {
        cancelToken: new CancelToken(c => {
            // An executor function receives a cancel function as a parameter
            cancelRequest = c;
        }),
    });
}

function handleFetchPopupContentError(e) {
    if (!axios.isCancel(e)) {
        throw e;
    }
}

/**
 * Handle openings and closings of map info windows
 */
export default class InfoWindowManager {
    /**
     *
     * @param {MapboxGL.Map} map
     * @param {Element} wrapperElement
     * @param {Function} initialInfoWindowTemplate template to generate default infowindow
     */
    constructor(map, wrapperElement, initialInfoWindowTemplate = null) {
        this.map = map;
        this.wrapperElement = wrapperElement;
        this.$infoWindow = $(this.wrapperElement).find('[data-map-infowindow]');

        if (initialInfoWindowTemplate && isMobile()) {
            this.updateInfoWindowContent(initialInfoWindowTemplate(), false);
            this.show();
        }
    }

    /**
     * When user hover a feature marker (colored dot) on symbol layer, fetch popup content
     * @param {Object} feature
     */
    loadMarkerInfoWindow(feature, params) {
        if (isMobile()) {
            this.$infoWindow.show();
            this.updateInfoWindowContent(spinnerTemplate({ classes: 'spinner--dots' }), false);

            fetchPopupContent(feature, params)
                .then(response => {
                    this.updateInfoWindowContent(response.data, true);
                    this.$infoWindow.trigger({
                        type: MAEvent.mapFeatureInfoWindowOpened,
                        feature,
                    });
                })
                .catch(handleFetchPopupContentError);

            return;
        }

        // Else Desktop
        this.map.getCanvas().style.cursor = 'pointer';
        if (!this.markerPopup) {
            this.markerPopup = new MapboxGL.Popup({
                closeOnClick: false,
                maxWidth: 'none', // new mapbox-gl version use a default maxWidth of 240px
            });
        }
        this.markerPopup
            .setLngLat(feature.geometry.coordinates)
            .setHTML(spinnerTemplate({ classes: 'spinner--dots' }))
            .addTo(this.map);

        // cancel the request
        if (cancelRequest) {
            cancelRequest();
        }

        fetchPopupContent(feature, params)
            .then(response => {
                if (!this.markerPopup) {
                    return; // Popup has been hide during request
                }
                this.markerPopup.setHTML(response.data);
                this.initClusterNavigation();
                $(this.markerPopup.getElement()).trigger({
                    type: MAEvent.mapFeatureInfoWindowOpened,
                    feature,
                });
            })
            .catch(handleFetchPopupContentError);
    }

    /**
     * show the info window
     */
    show() {
        this.$infoWindow.show();
    }

    /**
     * Make pagination on a clustered infowindow
     */
    initClusterNavigation() {
        let currentPage = 1;
        const $infoWindow = $(this.wrapperElement).find('[data-infowindow-cluster]');
        if ($infoWindow.length === 0) {
            return;
        }
        const hiddenClass = $infoWindow.data('hidden-class');
        const $pages = $infoWindow.find('[data-infowindow-cluster-item]');
        const $currentPage = $infoWindow.find('[data-current-page]');
        const navPrevDisabled = $infoWindow.data('nav-prev-disabled-class');
        const navNextDisabled = $infoWindow.data('nav-next-disabled-class');
        const $pageCount = $infoWindow.find('[data-page-count]');
        const $prevButton = $infoWindow.find('[data-cluster-prev]');
        const $nextButton = $infoWindow.find('[data-cluster-next]');
        const pageCount = $pages.length;
        $pageCount.html(pageCount);

        const changePage = offset => {
            if (offset !== 1 && offset !== -1) {
                throw new Error('`changePage` argument should be `1` or `-1`');
            }
            if (currentPage === 1 && offset === -1) {
                return;
            }
            if (currentPage === pageCount && offset === 1) {
                return;
            }
            currentPage += offset;
            $currentPage.html(currentPage);
            $pages.each(index => {
                if (index === currentPage - 1) {
                    $pages.eq(index).removeClass(hiddenClass);
                } else {
                    $pages.eq(index).addClass(hiddenClass);
                }
            });
            $prevButton.removeClass(navPrevDisabled);
            $nextButton.removeClass(navNextDisabled);
            if (currentPage === 1) {
                $prevButton.addClass(navPrevDisabled);
            }
            if (currentPage === pageCount) {
                $nextButton.addClass(navNextDisabled);
            }
        };

        $prevButton.on('click', event => {
            event.preventDefault();
            changePage(-1);
        });

        $nextButton.on('click', event => {
            event.preventDefault();
            changePage(1);
        });
    }

    /**
     * When user leave a feature marker, hide popup
     */
    hideMarkerInfoWindow() {
        this.map.getCanvas().style.cursor = '';
        if (this.markerPopup) {
            this.markerPopup.remove();
            this.markerPopup = null;
        }
        this.updateInfoWindowContent(null);
    }

    /**
     * Fill the infowwindow with a content
     * @param {String} content
     * @param {Boolean} showCloseButton Add a close button
     */
    updateInfoWindowContent(content, showCloseButton = true) {
        if (!isMobile()) {
            return;
        }
        this.$infoWindow.html(content);

        if (showCloseButton) {
            const closeButton = $(
                '<a class="vector-map__infowindow__close"><i class="icon icon-close"></i></a>',
            );
            closeButton.on('click', event => {
                event.preventDefault();
                this.updateInfoWindowContent(null);
                this.$infoWindow.hide();
            });
            this.$infoWindow.append(closeButton);
        }
        this.initClusterNavigation();
    }
}
